import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type {
  UnifiedOrderRequest,
  PackageInput,
  SelectedRate,
  PickupAddOn,
} from "@/api/unified-orders";
import { useGetQuote } from "@/api/get-quote";
import {
  useAttachablePickupBookings,
  type AttachablePickupBooking,
} from "@/api/attachable-pickup-bookings";
import type { PricingOption, GetQuoteRequest } from "@/api.types.v2";
import {
  DollarSign,
  Package,
  Clock,
  AlertCircle,
  MapPin,
  Calendar,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

interface PricingSelectionStepProps {
  data: Partial<UnifiedOrderRequest>;
  onNext: (data: Partial<UnifiedOrderRequest>) => void;
  onBack: () => void;
}

/**
 * Pricing-redesign: pricing selection step.
 *
 * Replaces the legacy 4-bucket ServiceType selection with the new model:
 *   - Each option is a (carrier × carrierService) pair returned from /pricing/get-quote.
 *   - Pickup is a separate add-on. Server returns a per-option pickupOptions
 *     array of tier choices (empty if pickup unavailable); each tier carries
 *     its own timeSlots list.
 *   - The user toggles "include pickup" once per shipment + picks a tier; if
 *     on, the chosen tier's pickupPriceCents is added to the total and the
 *     time-slot dropdown is scoped to that tier's slots.
 *
 * On continue, we build a `selectedRate` echo (carrierId + carrierServiceCode +
 * service-area IDs + labelPriceCents + currency + opaque purchaseHandle) and an
 * optional `pickup` add-on. Server uses the echo for drift detection — diverging
 * > 5% returns 409 PRICING_DRIFT, handled in the parent dialog.
 */

// Synthetic option key: (carrierId, carrierServiceCode) is stable per option
function optionKey(opt: PricingOption): string {
  return `${opt.carrier.id}__${opt.carrierService.code}`;
}

export function PricingSelectionStep({
  data,
  onNext,
  onBack,
}: PricingSelectionStepProps) {
  // TODO(carrier-scope): /pricing/get-quote returns cross-carrier options. To
  // pre-filter by the selected carriers we'd need either a server-side filter
  // on get-quote or to drop non-matching options after the fact. Wizard
  // refactor is out of scope for this PR — wire when get-quote gets a
  // carrierIds/carrierCodes filter.
  const { carrierIds: scopedCarrierIds } = useCarrierScope();
  void scopedCarrierIds;
  // Per-shipment selection: optionKey + includePickup boolean
  const [selectedKeys, setSelectedKeys] = useState<Record<number, string>>({});
  const [includePickup, setIncludePickup] = useState<Record<number, boolean>>(
    {}
  );
  const [pricingData, setPricingData] = useState<
    Record<number, PricingOption[]>
  >({});
  const [rejectionReasonsByIndex, setRejectionReasonsByIndex] = useState<
    Record<number, string[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pickup schedule (single date+slot used across shipments that opted in)
  const [pickupDate, setPickupDate] = useState<string>("");
  const [pickupTimeSlotId, setPickupTimeSlotId] = useState<string>("");
  // Per-shipment selected pickup tier (pickupServiceId). Auto-picked in a
  // render-phase pattern below when the selected option's tier set changes.
  const [selectedPickupServiceIds, setSelectedPickupServiceIds] = useState<
    Record<number, string>
  >({});
  // Tracks the prev tier-set "key" per shipment so the render-phase auto-pick
  // only fires when the option's tier set actually changes.
  const [prevTierKeys, setPrevTierKeys] = useState<Record<number, string>>({});

  // Phase 10b: when a single-shipment order picks pickup AND its sender came
  // from AddressBook, surface attachable upcoming bookings. Multi-shipment
  // orders skip the chooser — the global date+slot model doesn't compose with
  // per-booking date selection. Admin still gets entry-ID forwarding (so
  // PickupBooking dedup fires server-side if dates happen to align), but the
  // explicit chooser is single-shipment only.
  const singleShipmentEntryId =
    data.shipments?.length === 1
      ? data.shipments[0].shipFromAddressBookEntryId
      : undefined;
  const { data: attachableData } = useAttachablePickupBookings({
    customerId: data.placerCustomerId,
    addressBookEntryId: singleShipmentEntryId,
  });
  const attachableBookings = attachableData?.data ?? [];
  const [pickupMode, setPickupMode] = useState<"attach" | "new">("new");
  const [selectedAttachableBookingId, setSelectedAttachableBookingId] =
    useState<string | undefined>();

  useEffect(() => {
    if (attachableBookings.length === 0) {
      setPickupMode("new");
      setSelectedAttachableBookingId(undefined);
      return;
    }
    setPickupMode("attach");
    if (!selectedAttachableBookingId) {
      setSelectedAttachableBookingId(attachableBookings[0].id);
    }
  }, [attachableBookings, selectedAttachableBookingId]);

  const selectedAttachable: AttachablePickupBooking | undefined =
    attachableBookings.find((b) => b.id === selectedAttachableBookingId);
  const fetchingRef = useRef(false);

  const quoteMutation = useGetQuote();

  // Default pickup date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPickupDate(tomorrow.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    if (!fetchingRef.current && data.shipments && data.shipments.length > 0) {
      fetchingRef.current = true;
      void fetchAllQuotes();
    }
  }, [data.shipments]);

  // When the user switches pickup tiers, the previously-chosen time slot may
  // not exist in the new tier's timeSlots. Clear it so the dropdown reverts to
  // the placeholder and the user explicitly re-picks. Slot is shared across
  // shipments, so any active tier missing it makes the slot invalid.
  useEffect(() => {
    if (!pickupTimeSlotId) return;
    for (const [idxStr, tierId] of Object.entries(selectedPickupServiceIds)) {
      const idx = parseInt(idxStr, 10);
      if (includePickup[idx] !== true) continue;
      const key = selectedKeys[idx];
      const opt = key
        ? pricingData[idx]?.find((o) => optionKey(o) === key)
        : undefined;
      const tier = opt?.pickupOptions.find((t) => t.pickupServiceId === tierId);
      if (!tier) continue;
      if (!tier.timeSlots.some((s) => s.id === pickupTimeSlotId)) {
        setPickupTimeSlotId("");
        return;
      }
    }
  }, [
    pickupTimeSlotId,
    selectedPickupServiceIds,
    includePickup,
    selectedKeys,
    pricingData,
  ]);

  const fetchAllQuotes = async () => {
    if (!data.shipments || data.shipments.length === 0) {
      setError("No shipments to price");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const promises = data.shipments.map(async (shipment, index) => {
        // Phase 10a.5 made shipFrom/shipTo optional on the API type (XOR with
        // entry IDs), but the form schema still requires them and the form
        // populates the inline fields even when an AddressBook entry is
        // selected. Assert non-null at this call site.
        const shipFrom = shipment.shipFrom!;
        const shipTo = shipment.shipTo!;
        const request: GetQuoteRequest = {
          shipFrom: {
            // The address Address type uses line1/province; map from form fields.
            // Server requires name OR company on each address — fold in the
            // sender contact details that live alongside shipFrom on the
            // shipment input.
            id: "",
            line1: shipFrom.street1,
            line2: shipFrom.street2,
            city: shipFrom.city,
            province: shipFrom.state,
            postalCode: shipFrom.postalCode,
            country: shipFrom.country,
            name: shipment.senderName,
            phone: shipment.senderPhone ?? undefined,
            email: shipment.senderEmail ?? undefined,
            createdAt: "",
            updatedAt: "",
          },
          shipTo: {
            id: "",
            line1: shipTo.street1,
            line2: shipTo.street2,
            city: shipTo.city,
            province: shipTo.state,
            postalCode: shipTo.postalCode,
            country: shipTo.country,
            name: shipment.recipientName,
            phone: shipment.recipientPhone ?? undefined,
            email: shipment.recipientEmail ?? undefined,
            createdAt: "",
            updatedAt: "",
          },
          packages: shipment.packages.map(toGetQuotePackage),
          customerId: data.placerCustomerId,
        };
        const response = await quoteMutation.mutateAsync(request);
        return { index, response };
      });

      const results = await Promise.all(promises);
      const byIndex: Record<number, PricingOption[]> = {};
      const reasonsByIndex: Record<number, string[]> = {};
      let inServiceArea = true;
      for (const { index, response } of results) {
        byIndex[index] = response.options;
        reasonsByIndex[index] = [
          ...new Set(
            response.rejectionReasons?.map((reason) => reason.reason) ?? []
          ),
        ];
        if (!response.addressesInServiceArea) inServiceArea = false;
      }
      setPricingData(byIndex);
      setRejectionReasonsByIndex(reasonsByIndex);
      if (!inServiceArea) {
        setError(
          "One or more addresses are outside our service area. Please verify the addresses."
        );
      }
    } catch (err) {
      console.error("Failed to fetch quotes:", err);
      setError("Failed to fetch pricing options. Please try again.");
      toast.error("Pricing error", {
        description: "Failed to fetch pricing options. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (shipmentIndex: number, key: string) => {
    setSelectedKeys((prev) => ({ ...prev, [shipmentIndex]: key }));
    // Reset pickup toggle if the new option doesn't support pickup
    const opt = findOption(shipmentIndex, key);
    if (opt?.pickupOptions.length === 0) {
      setIncludePickup((prev) => ({ ...prev, [shipmentIndex]: false }));
    }
  };

  const findOption = (
    shipmentIndex: number,
    key: string
  ): PricingOption | undefined => {
    return pricingData[shipmentIndex]?.find((o) => optionKey(o) === key);
  };

  // Single source of truth for which pickup tier applies to a shipment:
  //   - attach mode → the attached booking's pickupService (fixed; handleContinue
  //     submits this tier so display must use the same id)
  //   - otherwise  → the user-picked tier from the dropdown
  // Both the display total and the submission path go through this helper so
  // they can never drift.
  const effectivePickupServiceId = (shipmentIndex: number): string => {
    const attachActive =
      pickupMode === "attach" && selectedAttachable !== undefined;
    if (attachActive) return selectedAttachable.pickupService.id;
    return selectedPickupServiceIds[shipmentIndex] ?? "";
  };

  // True when in attach mode AND the attached booking's pickup tier isn't
  // priced on this option. In that case we cannot submit pickup (handleContinue
  // would leave `pickup: undefined`) so we must NOT show a fallback price —
  // otherwise the displayed total includes a pickup fee that won't be charged
  // and the order is submitted without pickup.
  const attachTierMissingFor = (
    _shipmentIndex: number,
    opt: PricingOption
  ): boolean => {
    const attachActive =
      pickupMode === "attach" && selectedAttachable !== undefined;
    if (!attachActive) return false;
    if (opt.pickupOptions.length === 0) return false;
    const tierId = selectedAttachable.pickupService.id;
    return !opt.pickupOptions.some((t) => t.pickupServiceId === tierId);
  };

  // Resolve the effective tier price for a shipment.
  //   - new mode: falls back to the cheapest tier when the effective tier id
  //     isn't yet in pickupOptions (render-phase auto-pick will fix it shortly)
  //   - attach mode: returns null when the attached booking's tier isn't
  //     priced on this option — the displayed total must NOT include a phantom
  //     pickup fee since handleContinue will submit `pickup: undefined`
  const pickupTaxFor = (shipmentIndex: number, opt: PricingOption): number => {
    if (opt.pickupOptions.length === 0) return 0;
    const tierId = effectivePickupServiceId(shipmentIndex);
    const tier = tierId
      ? opt.pickupOptions.find((t) => t.pickupServiceId === tierId)
      : undefined;
    return tier?.taxCents ?? 0;
  };
  const pickupPriceFor = (
    shipmentIndex: number,
    opt: PricingOption
  ): number | null => {
    if (opt.pickupOptions.length === 0) return null;
    const tierId = effectivePickupServiceId(shipmentIndex);
    const tier = tierId
      ? opt.pickupOptions.find((t) => t.pickupServiceId === tierId)
      : undefined;
    if (tier) return tier.pickupPriceCents;
    // Attach mode: no fallback — the attached tier is fixed and unpriceable
    // on this option, so the row is invalid. Continue button is gated below.
    if (attachTierMissingFor(shipmentIndex, opt)) return null;
    return Math.min(...opt.pickupOptions.map((t) => t.pickupPriceCents));
  };

  const anyPickupSelected = () =>
    Object.entries(selectedKeys).some(([idx, key]) => {
      const opt = findOption(parseInt(idx, 10), key);
      return (
        includePickup[parseInt(idx, 10)] === true &&
        opt != null &&
        opt.pickupOptions.length > 0
      );
    });

  // True when any shipment that wants pickup is attached to a booking whose
  // tier isn't priceable on the chosen option. Blocks Continue so the user
  // never submits an order whose displayed total disagrees with the wire.
  const anyAttachTierMissing = () =>
    Object.entries(selectedKeys).some(([idx, key]) => {
      const i = parseInt(idx, 10);
      const opt = findOption(i, key);
      if (!opt) return false;
      if (includePickup[i] !== true) return false;
      return attachTierMissingFor(i, opt);
    });

  // Split into subtotal + tax so the Pricing-summary panel can show a clean
  // breakdown. Tax-inclusive total = subtotalCents + taxCentsTotal.
  const { subtotalCents, taxCentsTotal } = (() => {
    let subtotal = 0;
    let tax = 0;
    Object.entries(selectedKeys).forEach(([idx, key]) => {
      const i = parseInt(idx, 10);
      const opt = findOption(i, key);
      if (!opt) return;
      subtotal += opt.labelPriceCents;
      tax += opt.taxCents;
      if (includePickup[i] === true && opt.pickupOptions.length > 0) {
        const price = pickupPriceFor(i, opt);
        if (price != null) {
          subtotal += price;
          tax += pickupTaxFor(i, opt);
        }
      }
    });
    return { subtotalCents: subtotal, taxCentsTotal: tax };
  })();
  const totalCents = subtotalCents + taxCentsTotal;

  const handleContinue = () => {
    if (!data.shipments || data.shipments.length === 0) return;

    const missing = data.shipments
      .map((_, i) => i)
      .filter((i) => !selectedKeys[i]);
    if (missing.length > 0) {
      toast.error("Selection required", {
        description: "Please select a pricing option for all shipments.",
      });
      return;
    }

    // Hard block: in attach mode, if the attached booking's tier isn't priced
    // on a chosen option, we'd submit pickup: undefined while the UI total
    // implied a pickup fee. Reject here so the user picks a compatible option.
    if (anyAttachTierMissing()) {
      toast.error("Pickup tier not available", {
        description:
          "The selected option doesn't price the attached booking's pickup service. Pick a different option.",
      });
      return;
    }

    // Phase 10b: when attaching to an existing booking, pull date+slot from
    // the chosen booking instead of the manual pickers.
    const attachActive =
      pickupMode === "attach" && selectedAttachable !== undefined;
    const effectivePickupDate = attachActive
      ? new Date(selectedAttachable.scheduledDate).toISOString().split("T")[0]
      : pickupDate;
    const effectivePickupSlotId = attachActive
      ? selectedAttachable.slot.id
      : pickupTimeSlotId;

    if (anyPickupSelected()) {
      if (!effectivePickupDate) {
        toast.error("Pickup date required", {
          description: "Please select a pickup date.",
        });
        return;
      }
      if (!effectivePickupSlotId) {
        toast.error("Pickup time required", {
          description: "Please select a pickup time slot.",
        });
        return;
      }
    }

    // Build selectedRate + optional pickup per shipment
    const shipmentsWithRates = data.shipments.map((shipment, index) => {
      const opt = findOption(index, selectedKeys[index]);
      if (!opt) return shipment;

      // The id, not the price. The server bills the quote row it wrote — sending a
      // number here would only be a number it ignores (#533).
      const selectedRate: SelectedRate = {
        quoteId: opt.quoteId,
        carrierId: opt.carrier.id,
        carrierServiceCode: opt.carrierService.code,
        fromServiceAreaId: opt.fromServiceAreaId,
        toServiceAreaId: opt.toServiceAreaId,
        currency: opt.currency,
      };

      // Phase 10b: in attach mode, the booking's pickupService is fixed —
      // using a different tier would miss the server's find-or-attach unique
      // key (pickupAddressId, scheduledDate, slotId, pickupServiceId) and
      // silently create a parallel booking. The effectivePickupServiceId
      // helper (used by display too) returns the attached booking's tier id
      // when attaching, else the user-picked tier — single source of truth so
      // display totals can't drift from what we submit.
      let pickup: PickupAddOn | undefined;
      if (includePickup[index] === true && opt.pickupOptions.length > 0) {
        const tierId = effectivePickupServiceId(index);
        if (tierId) {
          const tier = opt.pickupOptions.find(
            (t) => t.pickupServiceId === tierId
          );
          // If the attached booking's tier isn't available on this option anymore
          // (e.g. deactivated), leave pickup undefined so existing validation
          // surfaces the mismatch rather than booking a tier that isn't on offer.
          if (tier) {
            pickup = {
              pickupServiceId: tier.pickupServiceId,
              scheduledDate: effectivePickupDate,
              slotId: effectivePickupSlotId,
            };
          }
        }
      }

      // Tax-inclusive shipment total (label + label tax + pickup + pickup tax).
      // Downstream views (dialog footer, payment-selection) read priceCents
      // and need it to match the actual Stripe charge.
      const pickupTier = pickup
        ? opt.pickupOptions.find(
            (t) => t.pickupServiceId === pickup.pickupServiceId
          )
        : undefined;
      const totalForShipment =
        opt.labelPriceCents +
        opt.taxCents +
        (pickupTier ? pickupTier.pickupPriceCents + pickupTier.taxCents : 0);

      return {
        ...shipment,
        selectedRate,
        pickup,
        // Mirror old shape for the dialog's summary calc
        carrierId: opt.carrier.id,
        selectedPricing: {
          priceCents: totalForShipment,
          currency: opt.currency,
          serviceName: opt.carrierService.name,
          carrierName: opt.carrier.name,
        },
      };
    });

    onNext({
      shipments: shipmentsWithRates,
      metadata: data.metadata,
    } as Parameters<typeof onNext>[0]);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading pricing options…</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricing error</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex gap-2 mt-4">
            <Button onClick={onBack} variant="outline">
              Back
            </Button>
            <Button onClick={() => void fetchAllQuotes()}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            Shipping options
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Pick a carrier service per shipment. Pickup is an optional add-on.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void fetchAllQuotes()}
          disabled={loading}
          className="shrink-0"
        >
          <RefreshCw
            className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {data.shipments?.map((shipment, shipmentIndex) => {
        const options = pricingData[shipmentIndex] ?? [];
        const selectedKey = selectedKeys[shipmentIndex] ?? "";
        const selectedOpt = findOption(shipmentIndex, selectedKey);
        const supportsPickup = (selectedOpt?.pickupOptions.length ?? 0) > 0;
        const includePu = includePickup[shipmentIndex] === true;
        const selectedPickupServiceId =
          selectedPickupServiceIds[shipmentIndex] ?? "";
        // Effective tier id may be the attach-booking's tier rather than the
        // dropdown selection — go through the helper so display matches submit.
        const effectiveTierId = effectivePickupServiceId(shipmentIndex);
        const selectedTier = selectedOpt?.pickupOptions.find(
          (t) => t.pickupServiceId === effectiveTierId
        );

        // Auto-select a pickup tier when this shipment's option's tier set
        // changes. Render-phase pattern: track prev tier-set key per shipment.
        // 0 tiers → empty; current selection still valid → leave alone;
        // otherwise pick the first tier.
        const tierKey = (selectedOpt?.pickupOptions ?? [])
          .map((p) => p.pickupServiceId)
          .join("|");
        const prevTierKey = prevTierKeys[shipmentIndex] ?? "";
        if (tierKey !== prevTierKey) {
          // Schedule the state updates outside render — but React allows
          // calling setState during render as long as we read prev state via
          // updater fns. Using the queueing pattern here.
          setPrevTierKeys((prev) => ({ ...prev, [shipmentIndex]: tierKey }));
          const tiers = selectedOpt?.pickupOptions ?? [];
          if (tiers.length === 0) {
            setSelectedPickupServiceIds((prev) => {
              if (!prev[shipmentIndex]) return prev;
              const next = { ...prev };
              delete next[shipmentIndex];
              return next;
            });
          } else if (
            !tiers.some((t) => t.pickupServiceId === selectedPickupServiceId)
          ) {
            setSelectedPickupServiceIds((prev) => ({
              ...prev,
              [shipmentIndex]: tiers[0].pickupServiceId,
            }));
          }
        }

        // Per-shipment Cheapest / Fastest recommendations.
        const cheapestOpt =
          options.length > 0
            ? [...options].sort(
                (a, b) => a.labelPriceCents - b.labelPriceCents
              )[0]
            : null;
        const fastestOpt =
          options.length > 0
            ? [...options].sort(
                (a, b) =>
                  (a.carrierService.estimatedDays ?? 99) -
                  (b.carrierService.estimatedDays ?? 99)
              )[0]
            : null;

        return (
          <Card key={shipmentIndex}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Shipment {shipmentIndex + 1} pricing
                </CardTitle>
                {data.shipments && data.shipments.length > 1 && (
                  <Badge variant="outline">
                    {shipmentIndex + 1} of {data.shipments.length}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  <span>
                    From: {shipment.shipFrom?.city ?? "—"},{" "}
                    {shipment.shipFrom?.state ?? ""}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  <span>
                    To: {shipment.shipTo?.city ?? "—"},{" "}
                    {shipment.shipTo?.state ?? ""}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(rejectionReasonsByIndex[shipmentIndex]?.length ?? 0) > 0 && (
                <Alert role="status" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <p>Some shipping options are unavailable:</p>
                    <ul className="list-disc pl-5">
                      {rejectionReasonsByIndex[shipmentIndex].map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              {options.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No pricing options available for this route. Please verify
                    the addresses.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                  {/* Compact options list (3/5 width on desktop, single
                      column on mobile). Mirrors the customer-web pricing UI:
                      single row per option, inline badge, breakdown shown
                      under the description on the left. */}
                  <div className="lg:col-span-3 border rounded-md overflow-hidden divide-y">
                    {options.map((opt) => {
                      const key = optionKey(opt);
                      const isSelected = selectedKey === key;
                      const isCheapest =
                        cheapestOpt && key === optionKey(cheapestOpt);
                      const isFastest =
                        fastestOpt && key === optionKey(fastestOpt);
                      // Row headline is pre-tax — single comparable axis
                      // across carriers. Pickup is shown separately in the
                      // right-hand panel; tax is broken out once in the
                      // Pricing summary below.
                      const total = opt.labelPriceCents;

                      // Pick at most one priority badge (Fastest > Cheapest >
                      // Pickup avail) to keep the row uncluttered.
                      const badge = isFastest
                        ? {
                            label: "Fastest",
                            icon: <Clock className="h-3 w-3" />,
                          }
                        : isCheapest
                          ? {
                              label: "Cheapest",
                              icon: <Sparkles className="h-3 w-3" />,
                            }
                          : null;

                      return (
                        <div
                          key={key}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelect(shipmentIndex, key)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleSelect(shipmentIndex, key);
                            }
                          }}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-primary/5 ring-1 ring-inset ring-primary"
                              : "hover:bg-muted/40"
                          }`}
                        >
                          {/* Selection indicator */}
                          <div
                            className={`h-3 w-3 rounded-full border shrink-0 ${
                              isSelected
                                ? "bg-primary border-primary"
                                : "border-muted-foreground/30"
                            }`}
                          />

                          {/* Carrier + service + inline badge */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium truncate">
                                {opt.carrier.name}
                              </span>
                              <span className="text-xs text-muted-foreground truncate">
                                {opt.carrierService.name}
                              </span>
                              {badge && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] flex items-center gap-1 px-1.5 py-0 shrink-0"
                                >
                                  {badge.icon}
                                  {badge.label}
                                </Badge>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {opt.carrierService.estimatedDays != null
                                ? `Est. ${opt.carrierService.estimatedDays} business day${
                                    opt.carrierService.estimatedDays > 1
                                      ? "s"
                                      : ""
                                  }`
                                : "Contact for details"}
                            </div>
                          </div>

                          {/* Price */}
                          <div className="text-right shrink-0">
                            <div className="text-base font-semibold leading-tight">
                              {formatPrice(total)}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {opt.currency}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pickup add-on panel (2/5 width on desktop, stacks
                      below on mobile). Per-shipment because admin can have
                      multiple shipments with different supports-pickup
                      states. Date/slot/attach chooser live INSIDE this panel
                      to mirror customer-web's tighter layout. In multi-shipment
                      orders, the same date+slot state is shared across all
                      panels — editing one updates them all. */}
                  <aside className="lg:col-span-2">
                    <Card className="h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Pickup add-on
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-0">
                        {!selectedOpt ? (
                          <p className="text-xs text-muted-foreground">
                            Pick a shipping option to see pickup availability.
                          </p>
                        ) : !supportsPickup ? (
                          <p className="text-xs text-muted-foreground">
                            Pickup is not available for this option. Drop off at
                            a designated location.
                          </p>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-2">
                              <div className="space-y-0.5 min-w-0">
                                <Label className="text-sm font-medium">
                                  Add pickup at sender's address
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {attachTierMissingFor(
                                    shipmentIndex,
                                    selectedOpt
                                  )
                                    ? "Tier unavailable on this option"
                                    : `+${formatPrice(
                                        pickupPriceFor(
                                          shipmentIndex,
                                          selectedOpt
                                        ) ?? 0
                                      )}${
                                        selectedOpt.pickupOptions.length > 1
                                          ? " (varies by tier)"
                                          : ""
                                      }`}
                                </p>
                              </div>
                              <Switch
                                checked={includePu}
                                onCheckedChange={(checked) =>
                                  setIncludePickup((prev) => ({
                                    ...prev,
                                    [shipmentIndex]: checked,
                                  }))
                                }
                              />
                            </div>
                            {includePu &&
                              attachTierMissingFor(
                                shipmentIndex,
                                selectedOpt
                              ) && (
                                <Alert variant="destructive">
                                  <AlertCircle className="h-4 w-4" />
                                  <AlertDescription className="text-xs">
                                    The attached booking's pickup service isn't
                                    priced on this option. Pick a different
                                    shipping option to keep this pickup.
                                  </AlertDescription>
                                </Alert>
                              )}

                            {includePu && (
                              <div className="space-y-3 pt-3 border-t">
                                {/* Phase 10b: attach-to-existing chooser. Only
                                    when single-shipment + sender from
                                    AddressBook + matching upcoming bookings. */}
                                {attachableBookings.length > 0 && (
                                  <div className="space-y-2">
                                    <label className="flex items-start gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="adminPickupMode"
                                        className="mt-1"
                                        checked={pickupMode === "attach"}
                                        onChange={() => setPickupMode("attach")}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">
                                          Attach to existing pickup
                                        </div>
                                        {pickupMode === "attach" &&
                                          selectedAttachable && (
                                            <div className="mt-2 rounded-md border bg-muted/30 p-3 space-y-2">
                                              <div className="text-sm font-medium">
                                                {new Date(
                                                  selectedAttachable.scheduledDate
                                                ).toLocaleDateString(
                                                  undefined,
                                                  {
                                                    weekday: "short",
                                                    month: "short",
                                                    day: "numeric",
                                                  }
                                                )}
                                                , {selectedAttachable.slot.name}{" "}
                                                (
                                                {
                                                  selectedAttachable.slot
                                                    .startTime
                                                }{" "}
                                                –{" "}
                                                {
                                                  selectedAttachable.slot
                                                    .endTime
                                                }
                                                )
                                              </div>
                                              <div className="text-xs text-muted-foreground">
                                                {
                                                  selectedAttachable.attachedShipmentCount
                                                }{" "}
                                                shipment
                                                {selectedAttachable.attachedShipmentCount ===
                                                1
                                                  ? ""
                                                  : "s"}{" "}
                                                already on this run
                                              </div>
                                              {selectedAttachable.notes && (
                                                <div className="text-xs">
                                                  <div className="font-medium text-muted-foreground">
                                                    Notes on file:
                                                  </div>
                                                  <div className="mt-0.5">
                                                    {selectedAttachable.notes}
                                                  </div>
                                                </div>
                                              )}
                                              {attachableBookings.length >
                                                1 && (
                                                <Select
                                                  value={
                                                    selectedAttachableBookingId
                                                  }
                                                  onValueChange={
                                                    setSelectedAttachableBookingId
                                                  }
                                                >
                                                  <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue placeholder="Pick a different existing pickup" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {attachableBookings.map(
                                                      (b) => (
                                                        <SelectItem
                                                          key={b.id}
                                                          value={b.id}
                                                        >
                                                          {new Date(
                                                            b.scheduledDate
                                                          ).toLocaleDateString(
                                                            undefined,
                                                            {
                                                              month: "short",
                                                              day: "numeric",
                                                            }
                                                          )}
                                                          , {b.slot.name}
                                                        </SelectItem>
                                                      )
                                                    )}
                                                  </SelectContent>
                                                </Select>
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    </label>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="adminPickupMode"
                                        className="mt-1"
                                        checked={pickupMode === "new"}
                                        onChange={() => setPickupMode("new")}
                                      />
                                      <div className="flex-1">
                                        <div className="text-sm font-medium">
                                          Schedule a new pickup
                                        </div>
                                      </div>
                                    </label>
                                  </div>
                                )}

                                {pickupMode === "new" && (
                                  <>
                                    {/* Pickup tier picker: auto-selects when 1
                                        tier; dropdown when 2+. Hidden when 0
                                        (supportsPickup already gates the
                                        whole pickup block). Also hidden in
                                        attach mode — the attached booking's
                                        pickupService is fixed and picking a
                                        different tier would miss the server's
                                        find-or-attach unique key. */}
                                    <div className="space-y-1">
                                      <Label
                                        htmlFor={`pickup-tier-${shipmentIndex}`}
                                        className="text-xs"
                                      >
                                        Pickup service
                                      </Label>
                                      {selectedOpt.pickupOptions.length ===
                                      1 ? (
                                        <div className="text-sm">
                                          {
                                            selectedOpt.pickupOptions[0]
                                              .pickupServiceName
                                          }{" "}
                                          ·{" "}
                                          {formatPrice(
                                            selectedOpt.pickupOptions[0]
                                              .pickupPriceCents
                                          )}
                                        </div>
                                      ) : (
                                        <Select
                                          value={selectedPickupServiceId}
                                          onValueChange={(v) =>
                                            setSelectedPickupServiceIds(
                                              (prev) => ({
                                                ...prev,
                                                [shipmentIndex]: v,
                                              })
                                            )
                                          }
                                        >
                                          <SelectTrigger
                                            id={`pickup-tier-${shipmentIndex}`}
                                          >
                                            <SelectValue placeholder="Select pickup service" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {selectedOpt.pickupOptions.map(
                                              (tier) => (
                                                <SelectItem
                                                  key={tier.pickupServiceId}
                                                  value={tier.pickupServiceId}
                                                >
                                                  {tier.pickupServiceName} ·{" "}
                                                  {formatPrice(
                                                    tier.pickupPriceCents
                                                  )}
                                                </SelectItem>
                                              )
                                            )}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <Label
                                        htmlFor={`pickupDate-${shipmentIndex}`}
                                        className="text-xs"
                                      >
                                        Pickup date
                                      </Label>
                                      <Input
                                        id={`pickupDate-${shipmentIndex}`}
                                        type="date"
                                        value={pickupDate}
                                        onChange={(e) =>
                                          setPickupDate(e.target.value)
                                        }
                                        min={
                                          new Date().toISOString().split("T")[0]
                                        }
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label
                                        htmlFor={`pickupTimeSlot-${shipmentIndex}`}
                                        className="text-xs"
                                      >
                                        Pickup time slot
                                      </Label>
                                      <Select
                                        value={pickupTimeSlotId}
                                        onValueChange={setPickupTimeSlotId}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Pick a time slot" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(selectedTier?.timeSlots ?? [])
                                            .length === 0 ? (
                                            <SelectItem value="none" disabled>
                                              No pickup time slots available
                                            </SelectItem>
                                          ) : (
                                            (selectedTier?.timeSlots ?? []).map(
                                              (slot) => (
                                                <SelectItem
                                                  key={slot.id}
                                                  value={slot.id}
                                                >
                                                  {slot.name} ({slot.startTime}{" "}
                                                  – {slot.endTime})
                                                </SelectItem>
                                              )
                                            )
                                          )}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </aside>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Summary */}
      {Object.keys(selectedKeys).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Pricing summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(selectedKeys).map(([idxStr, key]) => {
                const idx = parseInt(idxStr, 10);
                const opt = findOption(idx, key);
                if (!opt) return null;
                const wantsPickup =
                  includePickup[idx] === true && opt.pickupOptions.length > 0;
                const pickupCents = wantsPickup
                  ? (pickupPriceFor(idx, opt) ?? 0)
                  : 0;
                // Pre-tax per-shipment line — tax is the dedicated Tax row
                // below so it lives in one place.
                const total = opt.labelPriceCents + pickupCents;
                return (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>
                      Shipment {idx + 1} — {opt.carrier.name} (
                      {opt.carrierService.name}){wantsPickup ? " + pickup" : ""}
                    </span>
                    <span className="font-medium">{formatPrice(total)}</span>
                  </div>
                );
              })}
              <div className="pt-2 border-t space-y-1">
                {taxCentsTotal > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Subtotal</span>
                      <span>{formatPrice(subtotalCents)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Tax</span>
                      <span>{formatPrice(taxCentsTotal)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between pt-1">
                  <span className="font-medium">Total</span>
                  <span className="text-lg font-bold">
                    {formatPrice(totalCents)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hidden trigger for sticky footer Continue. Disabled when any shipment
          is missing a selection OR when attach mode has a tier-mismatch on
          any pickup-on shipment (would otherwise submit pickup: undefined
          against a UI total that included a pickup fee). */}
      <button
        type="button"
        data-pricing-continue
        onClick={handleContinue}
        style={{ display: "none" }}
        disabled={
          Object.keys(selectedKeys).length !== data.shipments?.length ||
          anyAttachTierMissing()
        }
      />
    </div>
  );
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function toGetQuotePackage(p: PackageInput): GetQuoteRequest["packages"][0] {
  return {
    weight: p.weight,
    weightUnit: p.weightUnit,
    length: p.length,
    width: p.width,
    height: p.height,
    dimensionUnit: p.dimensionUnit,
    packageKind: p.packageKind ?? "PARCEL",
    description: p.description,
    declaredValueCents: p.declaredValueCents,
  };
}
